import React, { useState, useEffect } from 'react';
import { Modal, Select, InputNumber, Switch, Tabs, Alert, Button, Space, Divider } from 'antd';
import { SCENARIO_TEMPLATES, EQUIPMENT_CONSTRAINTS, ENERGY_SOURCES, MONTHLY_DISTRIBUTIONS, validateScenarioConfig, calculateDerivedParameters } from '../config/scenarioTemplates';
import { COLORS } from '../constants/theme';

/**
 * Scenario Configurator Component
 * UI for creating and editing simulation scenarios
 */
const ScenarioConfigurator = ({ visible, onClose, onSave, initialConfig = null }) => {
  const [config, setConfig] = useState(initialConfig || SCENARIO_TEMPLATES.SKAELSKOER_FULL.config);
  const [validation, setValidation] = useState({ isValid: true, errors: [], warnings: [] });
  const [derivedParams, setDerivedParams] = useState({});

  // Update derived parameters when config changes
  useEffect(() => {
    const derived = calculateDerivedParameters(config);
    setDerivedParams(derived);

    const validationResult = validateScenarioConfig(config);
    setValidation(validationResult);
  }, [config]);

  const handleSave = () => {
    if (validation.isValid) {
      onSave(config);
      onClose();
    }
  };

  const loadTemplate = (templateId) => {
    const template = SCENARIO_TEMPLATES[templateId];
    if (template) {
      setConfig({ ...template.config });
    }
  };

  const updateConfig = (key, value) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  const updateMonthlyDistribution = (monthIndex, value) => {
    const newDistribution = [...(config.monthlyDistribution || new Array(12).fill(0))];
    newDistribution[monthIndex] = value;
    updateConfig('monthlyDistribution', newDistribution);
  };

  const tabItems = [
    {
      key: 'template',
      label: 'Vælg Skabelon',
      children: (
        <div style={{ padding: '1rem' }}>
          <h3 style={{ marginBottom: '1rem' }}>Forudefinerede Scenarier</h3>
          <Space direction="vertical" style={{ width: '100%' }}>
            {Object.entries(SCENARIO_TEMPLATES).map(([key, template]) => (
              <Button
                key={key}
                block
                size="large"
                onClick={() => loadTemplate(key)}
                style={{
                  textAlign: 'left',
                  height: 'auto',
                  padding: '1rem',
                  background: COLORS.ui.surface,
                  border: `2px solid ${COLORS.ui.border}`,
                }}
              >
                <div>
                  <div style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>{template.name}</div>
                  <div style={{ color: COLORS.ui.text.secondary, marginTop: '0.5rem' }}>
                    {template.description}
                  </div>
                </div>
              </Button>
            ))}
          </Space>
        </div>
      ),
    },
    {
      key: 'demand',
      label: 'Varmebehov',
      children: (
        <div style={{ padding: '1rem' }}>
          <h3 style={{ marginBottom: '1rem' }}>Årligt Varmebehov</h3>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem' }}>Samlet Årligt Behov (GWh)</label>
            <InputNumber
              value={config.annualDemandGWh}
              onChange={(value) => updateConfig('annualDemandGWh', value)}
              min={0}
              max={100}
              step={0.1}
              style={{ width: '100%' }}
            />
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem' }}>Fordelings Metode</label>
            <Select
              value={config.demandProfile}
              onChange={(value) => {
                updateConfig('demandProfile', value);
                if (value === 'SKAELSKOER') {
                  updateConfig('monthlyDistribution', null);
                } else if (value === 'SMALL_15GWH') {
                  updateConfig('monthlyDistribution', MONTHLY_DISTRIBUTIONS.SMALL_15GWH.monthly);
                }
              }}
              style={{ width: '100%' }}
            >
              <Select.Option value="SKAELSKOER">Skælskør Real Data (time-for-time)</Select.Option>
              <Select.Option value="SMALL_15GWH">Lille Anlæg (månedlig fordeling)</Select.Option>
              <Select.Option value="CUSTOM">Brugerdefineret</Select.Option>
            </Select>
          </div>

          {config.monthlyDistribution && (
            <div>
              <h4 style={{ marginBottom: '1rem' }}>Månedlig Fordeling (GWh)</h4>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '0.75rem',
              }}>
                {['Jan', 'Feb', 'Mar', 'Apr', 'Maj', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'].map((month, idx) => (
                  <div key={month}>
                    <label style={{ fontSize: '0.875rem', marginBottom: '0.25rem', display: 'block' }}>{month}</label>
                    <InputNumber
                      value={config.monthlyDistribution[idx]}
                      onChange={(value) => updateMonthlyDistribution(idx, value)}
                      min={0}
                      step={0.25}
                      style={{ width: '100%' }}
                      size="small"
                    />
                  </div>
                ))}
              </div>

              <Alert
                message={`Total: ${config.monthlyDistribution.reduce((sum, val) => sum + val, 0).toFixed(2)} GWh`}
                type={Math.abs(config.monthlyDistribution.reduce((sum, val) => sum + val, 0) - config.annualDemandGWh) < 0.1 ? 'success' : 'warning'}
                style={{ marginTop: '1rem' }}
              />
            </div>
          )}

          <Divider />

          <h4 style={{ marginBottom: '1rem' }}>Beregnede Værdier</h4>
          <div style={{ background: COLORS.ui.background, padding: '1rem', borderRadius: '0.5rem' }}>
            <div style={{ marginBottom: '0.5rem' }}>
              <strong>NET Levering (mål):</strong> {derivedParams.netDeliveryTarget?.toFixed(2)} GWh
            </div>
            <div style={{ marginBottom: '0.5rem' }}>
              <strong>AB-værk (før grid loss):</strong> {derivedParams.abPlantTarget?.toFixed(2)} GWh
            </div>
            <div style={{ marginBottom: '0.5rem' }}>
              <strong>AN-værk (før prod. loss):</strong> {derivedParams.anPlantTarget?.toFixed(2)} GWh
            </div>
            <div style={{ marginBottom: '0.5rem' }}>
              <strong>Elforbrug nødvendigt:</strong> {derivedParams.electricityInputNeeded?.toFixed(2)} GWh
            </div>
            <div style={{ marginBottom: '0.5rem' }}>
              <strong>Gns. time-demand:</strong> {derivedParams.avgHourlyDemandMW?.toFixed(1)} MW
            </div>
            <div>
              <strong>Est. peak demand:</strong> {derivedParams.peakDemandMW?.toFixed(1)} MW
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'equipment',
      label: 'Dimensionering',
      children: (
        <div style={{ padding: '1rem' }}>
          <h3 style={{ marginBottom: '1rem' }}>Anlægs Dimensionering</h3>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem' }}>Elkedel Kapacitet (MW)</label>
            <Select
              value={config.heater_MW}
              onChange={(value) => updateConfig('heater_MW', value)}
              style={{ width: '100%' }}
            >
              {EQUIPMENT_CONSTRAINTS.heater.options.map(mw => (
                <Select.Option key={mw} value={mw}>{mw} MW</Select.Option>
              ))}
            </Select>
            <div style={{ fontSize: '0.875rem', color: COLORS.ui.text.secondary, marginTop: '0.25rem' }}>
              Kedel timer: {derivedParams.heaterUtilizationHours?.toFixed(0)} timer/år
            </div>
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem' }}>HTES Moduler (x 150 MWh)</label>
            <Select
              value={config.htes_modules}
              onChange={(value) => {
                updateConfig('htes_modules', value);
                updateConfig('htes_capacity_MWh', value * 150);
              }}
              style={{ width: '100%' }}
            >
              {EQUIPMENT_CONSTRAINTS.htes.options.map(num => (
                <Select.Option key={num} value={num}>{num} modul{num > 1 ? 'er' : ''} = {num * 150} MWh</Select.Option>
              ))}
            </Select>
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem' }}>LTES Kapacitet (MWh)</label>
            <InputNumber
              value={config.ltes_capacity_MWh}
              onChange={(value) => updateConfig('ltes_capacity_MWh', value)}
              min={100}
              max={500}
              step={5}
              style={{ width: '100%' }}
              disabled
            />
            <div style={{ fontSize: '0.875rem', color: COLORS.ui.text.secondary, marginTop: '0.25rem' }}>
              Fast værdi for nu
            </div>
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem' }}>Turbine Kapacitet (MW)</label>
            <Select
              value={config.turbine_MW}
              onChange={(value) => updateConfig('turbine_MW', value)}
              style={{ width: '100%' }}
            >
              {EQUIPMENT_CONSTRAINTS.turbine.options.map(mw => (
                <Select.Option key={mw} value={mw}>{mw} MW</Select.Option>
              ))}
            </Select>
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem' }}>Batteri Kapacitet (MW)</label>
            <Select
              value={config.battery_MW}
              onChange={(value) => {
                updateConfig('battery_MW', value);
                updateConfig('battery_MWh', value); // 1 hour storage
              }}
              style={{ width: '100%' }}
            >
              {EQUIPMENT_CONSTRAINTS.battery.options.map(mw => (
                <Select.Option key={mw} value={mw}>
                  {mw === 0 ? 'Ingen batteri' : `${mw} MW (${mw} MWh)`}
                </Select.Option>
              ))}
            </Select>
          </div>

          <Divider />

          <div style={{ background: COLORS.ui.background, padding: '1rem', borderRadius: '0.5rem' }}>
            <div style={{ marginBottom: '0.5rem' }}>
              <strong>Total Lager Kapacitet:</strong> {(config.htes_capacity_MWh + config.ltes_capacity_MWh).toFixed(0)} MWh
            </div>
            <div>
              <strong>Lager Dage:</strong> {derivedParams.storageCapacityDays?.toFixed(1)} dage
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'sources',
      label: 'Energikilder',
      children: (
        <div style={{ padding: '1rem' }}>
          <h3 style={{ marginBottom: '1rem' }}>Tilgængelige Energikilder</h3>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem' }}>Profil</label>
            <Select
              value={Object.keys(ENERGY_SOURCES).find(key =>
                ENERGY_SOURCES[key].grid === config.energySources?.grid &&
                ENERGY_SOURCES[key].solar === config.energySources?.solar &&
                ENERGY_SOURCES[key].wasteHeat === config.energySources?.wasteHeat
              )}
              onChange={(key) => updateConfig('energySources', ENERGY_SOURCES[key])}
              style={{ width: '100%' }}
            >
              {Object.entries(ENERGY_SOURCES).map(([key, source]) => (
                <Select.Option key={key} value={key}>{source.name}</Select.Option>
              ))}
            </Select>
          </div>

          <div style={{ background: COLORS.ui.background, padding: '1rem', borderRadius: '0.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
              <span>⚡ Elnet (inkl. TSO 135 DKK/MWh)</span>
              <Switch checked={config.energySources?.grid} disabled />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
              <span>☀️ Sol (spotpris uden TSO)</span>
              <Switch
                checked={config.energySources?.solar}
                onChange={(checked) => updateConfig('energySources', { ...config.energySources, solar: checked })}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span>♻️ Overskudsvarme (150 DKK/MWh)</span>
              <Switch
                checked={config.energySources?.wasteHeat}
                onChange={(checked) => updateConfig('energySources', { ...config.energySources, wasteHeat: checked })}
              />
            </div>
          </div>

          <Alert
            message="Grid-only mode"
            description="I grid-only mode vil simuleringen kun bruge elnet som energikilde. Dette er ideelt til dimensionering af nye anlæg."
            type="info"
            showIcon
            style={{ marginTop: '1rem' }}
          />
        </div>
      ),
    },
  ];

  return (
    <Modal
      title="Scenario Konfiguration"
      open={visible}
      onCancel={onClose}
      onOk={handleSave}
      okText="Gem Konfiguration"
      cancelText="Annuller"
      width={800}
      okButtonProps={{ disabled: !validation.isValid }}
    >
      {validation.errors.length > 0 && (
        <Alert
          message="Valideringsfejl"
          description={
            <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
              {validation.errors.map((error, idx) => (
                <li key={idx}>{error}</li>
              ))}
            </ul>
          }
          type="error"
          style={{ marginBottom: '1rem' }}
        />
      )}

      {validation.warnings.length > 0 && (
        <Alert
          message="Advarsler"
          description={
            <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
              {validation.warnings.map((warning, idx) => (
                <li key={idx}>{warning}</li>
              ))}
            </ul>
          }
          type="warning"
          style={{ marginBottom: '1rem' }}
        />
      )}

      <Tabs items={tabItems} />
    </Modal>
  );
};

export default ScenarioConfigurator;
